import { Ban, CheckCircle2, RotateCcw, Tags } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime } from '../../../../lib/formatDate';
import { maintenanceCategoriesApi } from '../buildingManagementApi';
import type { MaintenanceCategoryListItem } from '../buildingManagementTypes';
import { MaintenanceCategoryModal } from '../components/MaintenanceCategoryModal';

type TabKey = 'info';

const BREADCRUMB: BreadcrumbSegment[] = [
  'General Services',
  'Building Management',
  { label: 'Settings', to: '/general-services/building-management/settings' },
  { label: 'Categories', to: '/general-services/building-management/settings/categories' },
  'Category',
];

/**
 * One maintenance category. Thin, and that is the point: what it carries that the form cannot is
 * how many items are filed under it and who last touched it — what a reader wants before
 * renaming or deleting it.
 */
export function MaintenanceCategoryProfilePage() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();

  const [category, setCategory] = useState<MaintenanceCategoryListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      maintenanceCategoriesApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;
          setCategory(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this category.', 0));
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  /** An undo — no tick and no password. The server still refuses a name taken meanwhile. */
  async function restore() {
    if (!id) return;

    setRestoring(true);
    setError(null);

    try {
      await maintenanceCategoriesApi.restore(id);
      toast.success('Restored the category.');
      load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not restore this category.', 0));
    } finally {
      setRestoring(false);
    }
  }

  if (error && !category) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={BREADCRUMB} />
        <ErrorState message={error.message} onRetry={() => load()} />
      </div>
    );
  }

  if (loading && !category) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={BREADCRUMB} />
        <div className="flex flex-col gap-4 p-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  if (!category) return null;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={BREADCRUMB} />

      <ProfileHeaderShell
        id="maintenance-category-profile"
        header={
          <ProfileHeader
            id="maintenance-category-profile-header"
            icon={<Tags className="h-6 w-6" aria-hidden="true" />}
            title={category.name}
            meta={[`${category.itemCount} ${category.itemCount === 1 ? 'item is' : 'items are'} filed under it`]}
            status={
              <StatusText tone={category.isActive ? 'active' : 'inactive'}>
                {category.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={category.createdByName}
            createdAt={category.createdAt}
          />
        }
        banner={
          error ? (
            <ProfileBanner
              id="maintenance-category-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={error.message}
            />
          ) : category.isActive ? (
            <ProfileBanner
              id="maintenance-category-profile-banner"
              tone="slate"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="This category is offered on the maintenance item form."
            />
          ) : (
            <ProfileBanner
              id="maintenance-category-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This category is deleted. It is no longer offered, and the items already filed under it keep naming it."
              action={
                <PermissionGate permission="maintenance-categories.delete">
                  <Button
                    id="maintenance-category-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void restore()}
                    disabled={restoring}
                    loading={restoring}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="maintenance-category-profile-details"
          title="Details"
          onEdit={() => setEditing(true)}
          editPermission="maintenance-categories.update"
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Name', value: category.name },
              { label: 'Description', value: category.description },
              {
                label: 'Near Expiration window',
                value:
                  category.nearExpirationDays != null
                    ? `${category.nearExpirationDays} days`
                    : 'Not set — items take the window under Settings › Aging',
              },
              {
                label: 'Items filed under it',
                value: (
                  <span className={`font-mono tabular-nums ${category.itemCount === 0 ? 'text-slate-300' : ''}`}>
                    {category.itemCount}
                  </span>
                ),
              },
            ]}
          />
        </ProfileCard>

        <ProfileCard id="maintenance-category-profile-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: category.createdByName },
              { label: 'Created', value: formatDateTime(category.createdAt), mono: true },
              { label: 'Updated by', value: category.updatedByName },
              { label: 'Updated', value: category.updatedAt ? formatDateTime(category.updatedAt) : null, mono: true },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <MaintenanceCategoryModal
          open
          editing={category}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
